// Dependency-free Gregorian <-> Jalali (Shamsi/Persian) calendar conversion,
// plus Persian-digit formatting. Shared by the finance API (month/date query
// parsing) and the finance UI (Jalali pickers, date display).
//
// The conversion is the proleptic astronomical Jalali algorithm described by
// Kazimierz M. Borkowski ("The Persian calendar for 3000 years", 1984), which
// every widely-used Jalali calendar library implements. It works by mapping
// both calendars onto a single integer day count (days since a fixed epoch)
// and converting through that shared axis, rather than through fixed
// day-per-month tables (which cannot express the Jalali leap-year rule).
// Truncating (round-toward-zero) integer division -- the calendar math below
// relies on this specific rounding direction for negative intermediates
// (e.g. `gm - 8` when gm < 8); Math.floor division gives off-by-one results.
function intDiv(a, b) {
    return Math.trunc(a / b);
}
function mod(a, b) {
    return a - intDiv(a, b) * b;
}
// Jalali leap-year cycle breaks (Borkowski's algorithm). Each entry is a
// Jalali year at which the 33-year leap cycle "breaks" and resynchronizes;
// valid for Jalali years in [-61, 3177].
const JALALI_BREAKS = [
    -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
];
function jalCal(jy) {
    const breaks = JALALI_BREAKS;
    if (jy < breaks[0] || jy >= breaks[breaks.length - 1]) {
        throw new Error(`Jalali year out of supported range: ${jy}`);
    }
    const gregorianYear = jy + 621;
    let leapJ = -14;
    let jp = breaks[0];
    let jump = 0;
    for (let i = 1; i < breaks.length; i += 1) {
        const jm = breaks[i];
        jump = jm - jp;
        if (jy < jm)
            break;
        leapJ += intDiv(jump, 33) * 8 + intDiv(mod(jump, 33), 4);
        jp = jm;
    }
    let n = jy - jp;
    leapJ += intDiv(n, 33) * 8 + intDiv(mod(n, 33) + 3, 4);
    if (mod(jump, 33) === 4 && jump - n === 4)
        leapJ += 1;
    const leapG = intDiv(gregorianYear, 4) - intDiv((intDiv(gregorianYear, 100) + 1) * 3, 4) - 150;
    const march = 20 + leapJ - leapG;
    if (jump - n < 6)
        n = n - jump + intDiv(jump, 33) * 33;
    let leap = mod(mod(n + 1, 33) - 1, 4);
    if (leap === -1)
        leap = 4;
    return { leap, gregorianYear, march };
}
function gregorianToDayNumber(gy, gm, gd) {
    let d = intDiv((gy + intDiv(gm - 8, 6) + 100100) * 1461, 4) + intDiv(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
    d = d - intDiv(intDiv(gy + 100100 + intDiv(gm - 8, 6), 100) * 3, 4) + 752;
    return d;
}
function dayNumberToGregorian(dayNumber) {
    let j = 4 * dayNumber + 139361631;
    j = j + intDiv(intDiv(4 * dayNumber + 183187720, 146097) * 3, 4) * 4 - 3908;
    const i = intDiv(mod(j, 1461), 4) * 5 + 308;
    const day = intDiv(mod(i, 153), 5) + 1;
    const month = mod(intDiv(i, 153), 12) + 1;
    const year = intDiv(j, 1461) - 100100 + intDiv(8 - month, 6);
    return { year, month, day };
}
function jalaliToDayNumber(jy, jm, jd) {
    const cal = jalCal(jy);
    return gregorianToDayNumber(cal.gregorianYear, 3, cal.march) + (jm - 1) * 31 - intDiv(jm, 7) * (jm - 7) + jd - 1;
}
function dayNumberToJalali(dayNumber) {
    const gy = dayNumberToGregorian(dayNumber).year;
    let jy = gy - 621;
    const cal = jalCal(jy);
    const dayNumberOf1Farvardin = gregorianToDayNumber(cal.gregorianYear, 3, cal.march);
    let k = dayNumber - dayNumberOf1Farvardin;
    if (k >= 0) {
        if (k <= 185) {
            return { year: jy, month: 1 + intDiv(k, 31), day: mod(k, 31) + 1 };
        }
        k -= 186;
    }
    else {
        jy -= 1;
        k += 179;
        if (cal.leap === 1)
            k += 1;
    }
    return { year: jy, month: 7 + intDiv(k, 30), day: mod(k, 30) + 1 };
}
/** Converts a Gregorian calendar date to its Jalali equivalent. */
export function gregorianToJalali(year, month, day) {
    return dayNumberToJalali(gregorianToDayNumber(year, month, day));
}
/** Converts a Jalali calendar date to its Gregorian equivalent. */
export function jalaliToGregorian(year, month, day) {
    return dayNumberToGregorian(jalaliToDayNumber(year, month, day));
}
export function isLeapJalaliYear(year) {
    return jalCal(year).leap === 0;
}
/** Number of days in Jalali month `month` (1-12) of `year`; months 1-6 have 31, 7-11 have 30, 12 has 29 or 30 (leap). */
export function daysInJalaliMonth(year, month) {
    if (!Number.isInteger(month) || month < 1 || month > 12) {
        throw new Error(`Invalid Jalali month: ${month}`);
    }
    if (month <= 6)
        return 31;
    if (month <= 11)
        return 30;
    return isLeapJalaliYear(year) ? 30 : 29;
}
export function isValidJalaliDate(year, month, day) {
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day))
        return false;
    if (month < 1 || month > 12 || day < 1)
        return false;
    try {
        return day <= daysInJalaliMonth(year, month);
    }
    catch {
        return false;
    }
}
export const JALALI_MONTH_NAMES = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
];
const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
/** Replaces every ASCII digit in `input` with its Persian-digit equivalent; non-digit characters pass through unchanged. */
export function toPersianDigits(input) {
    return String(input).replace(/[0-9]/g, (digit) => PERSIAN_DIGITS[Number(digit)]);
}
function pad2(value) {
    return String(value).padStart(2, "0");
}
/** "YYYY-MM" in ASCII digits (machine value: API query params, <input> values). */
export function formatJalaliMonthKey(year, month) {
    return `${year}-${pad2(month)}`;
}
/** "YYYY-MM-DD" in ASCII digits (machine value: API payloads, <input> values). */
export function formatJalaliDateKey(year, month, day) {
    return `${formatJalaliMonthKey(year, month)}-${pad2(day)}`;
}
/** "۱۴۰۵/۰۶/۱۱" — Persian-digit display string. */
export function formatJalaliDateFa(year, month, day) {
    return toPersianDigits(`${year}/${pad2(month)}/${pad2(day)}`);
}
/** "۱۱ شهریور ۱۴۰۵" — Persian-digit display string with the month name spelled out. */
export function formatJalaliDateLong(year, month, day) {
    const monthName = JALALI_MONTH_NAMES[month - 1] ?? "";
    return `${toPersianDigits(day)} ${monthName} ${toPersianDigits(year)}`;
}
const JALALI_MONTH_KEY_PATTERN = /^(\d{4})-(\d{2})$/;
const JALALI_DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Parses a "YYYY-MM" Jalali month key, validating the month range (year range is left to the caller). */
export function parseJalaliMonthKey(value) {
    const match = JALALI_MONTH_KEY_PATTERN.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12)
        return null;
    return { year, month };
}
/** Parses and validates a "YYYY-MM-DD" Jalali date key against the real days-in-month for that year. */
export function parseJalaliDateKey(value) {
    const match = JALALI_DATE_KEY_PATTERN.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!isValidJalaliDate(year, month, day))
        return null;
    return { year, month, day };
}
/** The "YYYY-MM" Jalali month key containing the given instant, read via UTC calendar fields. */
export function jalaliMonthKeyForDate(date) {
    const jalali = gregorianToJalali(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
    return formatJalaliMonthKey(jalali.year, jalali.month);
}
/**
 * The exact [start, end) Gregorian UTC-midnight timestamp range covered by
 * Jalali month `month` (1-12) of `year`, for use as SQL range-query bounds.
 */
export function jalaliMonthToUtcRange(year, month) {
    const startG = jalaliToGregorian(year, month, 1);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const endG = jalaliToGregorian(nextYear, nextMonth, 1);
    return {
        start: new Date(Date.UTC(startG.year, startG.month - 1, startG.day)).toISOString(),
        end: new Date(Date.UTC(endG.year, endG.month - 1, endG.day)).toISOString(),
    };
}
